import type { ConceptAssistResponse, ConceptDraft, ConceptSuggestion } from '@/types/nf'
import type { AutopilotEstimate } from '@/api/autopilotApi'
import type { AutopilotCreationStage } from '@/components/project/autopilotCreation'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface ProjectWizardValues {
  title: string
  description: string
  core_theme: string
  content_type: 'fiction'
  length_form: 'short' | 'long'
  content_language: string
  genre_key: string
  target_words?: number
  chapter_word_target: number
  autopilot_enabled: boolean
  secondary_drivers: string[]
  logline: string
  world_time_period: string
  world_location: string
  world_atmosphere: string
  world_rules: string
  protagonist_name: string
  protagonist_name_en: string
  protagonist_motivation: string
  protagonist_inner_flaw: string
  protagonist_identity_situation: string
  antagonist_source: string
  tone_keynote: string
  narrative_perspective: string
}

export type ProjectDraftOutcome =
  | { kind: 'draft'; draft: ConceptDraft }
  | { kind: 'genre_fallback'; suggestions: ConceptSuggestion[] }

export const targetWordsByLength = { short: 30000, long: 800000 } as const

export function createDefaultProjectWizardValues(): ProjectWizardValues {
  return {
    title: '',
    description: '',
    core_theme: '',
    content_type: 'fiction',
    length_form: 'long',
    content_language: 'zh',
    genre_key: '',
    target_words: targetWordsByLength.long,
    chapter_word_target: 3500,
    autopilot_enabled: false,
    secondary_drivers: [],
    logline: '',
    world_time_period: '',
    world_location: '',
    world_atmosphere: '',
    world_rules: '',
    protagonist_name: '',
    protagonist_name_en: '',
    protagonist_motivation: '',
    protagonist_inner_flaw: '',
    protagonist_identity_situation: '',
    antagonist_source: '',
    tone_keynote: '',
    narrative_perspective: '',
  }
}

export function AutopilotCreationOption({
  enabled,
  targetWords,
  chapterWordTarget,
  estimate,
  estimateLoading = false,
  busyStage,
  errorStage,
  disabled = false,
  onEnabledChange,
  onTargetWordsChange,
  onChapterWordTargetChange,
}: {
  enabled: boolean
  targetWords: number | undefined
  chapterWordTarget: number
  estimate?: AutopilotEstimate
  estimateLoading?: boolean
  busyStage?: AutopilotCreationStage
  errorStage?: AutopilotCreationStage
  disabled?: boolean
  onEnabledChange: (enabled: boolean) => void
  onTargetWordsChange: (value: number | undefined) => void
  onChapterWordTargetChange: (value: number | undefined) => void
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? i18n.language
  const number = (value: number) => value.toLocaleString(locale)

  return (
    <section className="space-y-3 rounded-lg border bg-muted/20 p-4" aria-label={t('project.wizard.autopilot.title')}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="project-easy-target-words">{t('project.wizard.autopilot.targetWords')}</Label>
          <NumberInput
            id="project-easy-target-words"
            value={targetWords}
            min={1}
            disabled={disabled}
            onChange={onTargetWordsChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="project-easy-chapter-words">{t('project.wizard.autopilot.chapterWords')}</Label>
          <NumberInput
            id="project-easy-chapter-words"
            value={chapterWordTarget}
            min={1}
            disabled={disabled}
            onChange={onChapterWordTargetChange}
          />
        </div>
      </div>
      <label className="flex cursor-pointer items-start gap-3 text-sm">
        <input
          className="mt-1"
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(event) => onEnabledChange(event.target.checked)}
        />
        <span>
          <span className="font-medium">{t('project.wizard.autopilot.enable')}</span>
          <span className="mt-1 block text-xs text-muted-foreground">{t('project.wizard.autopilot.enableHint')}</span>
        </span>
      </label>
      {estimateLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t('project.wizard.autopilot.estimating')}
        </p>
      ) : estimate ? (
        <div className="grid gap-2 text-sm sm:grid-cols-2">
          <p>{t('project.wizard.autopilot.layout', { chapters: number(estimate.total_chapters), volumes: number(estimate.volume_count) })}</p>
          <p>{t('project.wizard.autopilot.firstVolume', { chapters: number(estimate.scoped_chapters), words: number(estimate.scoped_words) })}</p>
          <p>{t('project.wizard.autopilot.credits', { low: number(estimate.credits_low), high: number(estimate.credits_high) })}</p>
          <p>{t('project.wizard.autopilot.checkpoints', { end: number(estimate.first_volume_chapters) })}</p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('project.wizard.autopilot.estimateUnavailable')}</p>
      )}
      {busyStage ? (
        <p className="flex items-center gap-2 text-sm font-medium" role="status">
          <Loader2 className="size-4 animate-spin" />
          {t(`project.wizard.autopilot.stages.${busyStage}`)}
        </p>
      ) : null}
      {errorStage ? (
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertCircle className="size-4" />
          {t('project.wizard.autopilot.failedStage', { stage: t(`project.wizard.autopilot.stageNames.${errorStage}`) })}
        </p>
      ) : null}
    </section>
  )
}

export function projectDraftOutcome(response: ConceptAssistResponse): ProjectDraftOutcome {
  if (response.draft) return { kind: 'draft', draft: response.draft }
  return { kind: 'genre_fallback', suggestions: response.suggestions }
}

export function applyProjectDraft(values: ProjectWizardValues, draft: ConceptDraft): ProjectWizardValues {
  return {
    ...values,
    title: draft.title ?? values.title,
    core_theme: draft.core_theme ?? values.core_theme,
    genre_key: draft.genre_key ?? values.genre_key,
    secondary_drivers: draft.secondary_drivers ?? values.secondary_drivers,
    logline: draft.logline ?? values.logline,
    world_rules: draft.world_rules ?? values.world_rules,
    protagonist_name: draft.protagonist_name ?? values.protagonist_name,
    protagonist_name_en: draft.protagonist_name_en ?? values.protagonist_name_en,
    protagonist_motivation: draft.protagonist_motivation ?? values.protagonist_motivation,
    protagonist_inner_flaw: draft.protagonist_inner_flaw ?? values.protagonist_inner_flaw,
    protagonist_identity_situation: draft.protagonist_identity ?? values.protagonist_identity_situation,
    antagonist_source: draft.antagonist_source ?? values.antagonist_source,
    tone_keynote: draft.tone_keynote ?? values.tone_keynote,
    narrative_perspective: draft.perspective ?? values.narrative_perspective,
  }
}

export function EasyGenreFallback({
  suggestions,
  selectedGenreKey,
  label,
  onSelect,
}: {
  suggestions: ConceptSuggestion[]
  selectedGenreKey: string
  label: string
  onSelect: (suggestion: ConceptSuggestion) => void
}) {
  return (
    <section className="space-y-3" aria-label={label}>
      <p className="text-sm font-medium">{label}</p>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((suggestion) => (
          <Button
            key={suggestion.angle}
            type="button"
            size="sm"
            variant={selectedGenreKey === suggestion.angle ? 'default' : 'outline'}
            onClick={() => onSelect(suggestion)}
          >
            {suggestion.text}
          </Button>
        ))}
      </div>
    </section>
  )
}
